import {
  LTV_THRESHOLD,
  MAX_LOAN_AMOUNT,
  MAX_PROPERTY_VALUE,
  MIN_LOAN_AMOUNT,
  MIN_PROPERTY_VALUE,
} from '@hex/calculators-shared/constants';
import { InterestRatesRequest } from '@hex/calculators-shared/types';
import { getLTV } from '@hex/calculators-shared/utils';
import { trackCalculatorInit, useTrackCalculatorLoad } from '@hex/shared/tracking';
import { useEffect, useState } from 'react';
import { type RegisterOptions, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function useInterestRateCalculatorForm() {
  const { t } = useTranslation();
  const [ltv, setLtv] = useState<number>();
  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    reset,
    formState: { errors, submitCount, isDirty },
  } = useForm<InterestRatesRequest>({
    defaultValues: {
      propertyValue: '',
      loanAmount: '',
      partnership: '',
    },
  });
  const allFields = watch();
  const ltvHandler = (propertyValue: string, loanAmount: string) => {
    const calculatedLtv = getLTV(propertyValue, loanAmount);
    const maxValue = new Intl.NumberFormat('sv-se').format(Math.floor(parseInt(propertyValue) * (LTV_THRESHOLD / 100)));

    if (calculatedLtv && calculatedLtv > LTV_THRESHOLD) {
      setLtv(undefined);

      return t('personalInformation.inputs.loanAmount.rules.ltv', { calculatedLtv, maxValue });
    }

    setLtv(calculatedLtv);

    return undefined;
  };

  const propertyValueFieldRules = {
    required: t('personalInformation.inputs.propertyValue.requiredError'),
    min: {
      value: MIN_PROPERTY_VALUE,
      message: t('personalInformation.inputs.propertyValue.rules.min', { amount: MIN_PROPERTY_VALUE }),
    },
    max: {
      value: MAX_PROPERTY_VALUE,
      message: t('personalInformation.inputs.propertyValue.rules.max', { amount: MAX_PROPERTY_VALUE + 1 }),
    },
    onBlur: () => {
      const message = ltvHandler(allFields.propertyValue, allFields.loanAmount);
      submitCount === 0 && (message ? setError('loanAmount', { message }) : clearErrors('loanAmount'));
    },
    deps: 'loanAmount',
  };

  type LoanAmountFieldRules = Omit<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;

  const loanAmountFieldRules: LoanAmountFieldRules = {
    required: t('personalInformation.inputs.loanAmount.requiredError'),
    min: {
      value: MIN_LOAN_AMOUNT,
      message: t('personalInformation.inputs.loanAmount.rules.min', { amount: MIN_LOAN_AMOUNT }),
    },
    max: {
      value: MAX_LOAN_AMOUNT,
      message: t('personalInformation.inputs.loanAmount.rules.max', { amount: MAX_LOAN_AMOUNT + 1 }),
    },
    onBlur: () => {
      const message = ltvHandler(allFields.propertyValue, allFields.loanAmount);
      submitCount === 0 && (message ? setError('loanAmount', { message }) : clearErrors('loanAmount'));
    },
    validate: {
      ltv: (v, formValues) => ltvHandler(formValues.propertyValue, v),
    },
    deps: 'propertyValue',
  };

  const [isTracked, setIsTracked] = useState(false);
  const trackCalculatorStarted = () => {
    if (!isTracked) {
      trackCalculatorInit();
      setIsTracked(true);
    }
  };

  // focus on error functionality
  useEffect(() => {
    if (errors) {
      // errors need to be sorted according to keys in the form data type,
      // by default they are sorted in the order they were registered by RHF
      const order: Record<keyof InterestRatesRequest, number> = {
        propertyValue: 0,
        loanAmount: 1,
        partnership: 2,
      };
      const firstError = Object.keys(errors).sort(
        (a, b) => order[a as keyof InterestRatesRequest] - order[b as keyof InterestRatesRequest],
      )[0];
      document.getElementsByName(firstError)[0]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  useTrackCalculatorLoad('rate-calculator');

  return {
    control,
    handleSubmit,
    trackCalculatorStarted,
    reset,
    isDirty,
    propertyValueFieldRules,
    loanAmountFieldRules,
    ltv,
  };
}
